import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, useAction, useApi } from '../lib/api';
import { date, money, relative } from '../lib/format';
import { useSubsidiaries, useVendors } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { ApprovalRequest, Bill, BillLine, Entry, Line } from '../lib/types';
import { EntryLines, useActorLabel } from '../components/entry';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Empty, Field, Modal, PageHeader, Query, SourceBadge, StatusBadge, Table, Tabs, Td, Textarea, Th } from '../components/ui';

type Req = ApprovalRequest & { entity: { entry: Entry; lines: Line[] } | { bill: Bill; lines: BillLine[] } | null };
type Status = 'PENDING' | 'APPROVED' | 'REJECTED';

export function ApprovalsPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('PENDING');
  const q = useApi<Req[]>(`/api/approvals?status=${status}`);
  const pending = useApi<Req[]>('/api/approvals?status=PENDING');
  const focus = params.get('focus');

  return (
    <>
      <PageHeader
        title="Approvals"
        description="Journal entries and bills held by approval workflows. Anything proposed by the AI assistant, an MCP agent or the API waits here until a person decides."
      />
      <Tabs
        className="mb-4"
        value={status}
        onChange={setStatus}
        items={[
          { key: 'PENDING', label: 'Waiting', count: pending.data?.length },
          { key: 'APPROVED', label: 'Approved' },
          { key: 'REJECTED', label: 'Rejected' },
        ]}
      />
      <Query q={q}>
        {(rows) =>
          rows.length === 0 ? (
            <Card>
              <Empty title={status === 'PENDING' ? 'Inbox zero' : 'Nothing here yet'} icon={<Icons.Inbox size={28} />}>
                {status === 'PENDING' ? 'No entries or bills are waiting for approval.' : 'Decided requests show up here with who decided and why.'}
              </Empty>
            </Card>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => (
                <ApprovalCard key={r.id} r={r} focused={focus === r.id} />
              ))}
            </div>
          )
        }
      </Query>
    </>
  );
}

function ApprovalCard({ r, focused }: { r: Req; focused: boolean }) {
  const { me, org, can } = useSession();
  const actor = useActorLabel();
  const vendors = useVendors();
  const subs = useSubsidiaries();
  const [open, setOpen] = useState(focused || r.status === 'PENDING');
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  useEffect(() => {
    if (focused) document.getElementById(r.id)?.scrollIntoView({ block: 'center' });
  }, [focused, r.id]);

  const decide = useAction((d: { decision: 'APPROVE' | 'REJECT'; comment?: string }) => api(`/api/approvals/${r.id}/decide`, { body: d }), {
    success: (_o) => 'Decision recorded',
  });

  const e = r.entity;
  const isEntry = e && 'entry' in e;
  const isBill = e && 'bill' in e;
  const currency = isEntry ? e.entry.currency : isBill ? e.bill.currency : org.baseCurrency;
  const amount = isEntry ? e.lines.reduce((s, l) => s + l.debit, 0) : isBill ? e.bill.total : undefined;
  const title = isEntry ? `Journal entry #${e.entry.number}` : isBill ? `Bill ${e.bill.number} · ${vendors.byId.get(e.bill.vendorId)?.name ?? 'Vendor'}` : 'Deleted item';
  const ownRequest = r.requestedBy === me.user.id || r.requestedBy === `user:${me.user.id}`;
  const selfBlocked = ownRequest && !org.settings.allowSelfApproval;
  const canDecide = r.status === 'PENDING' && can(r.approverRole as 'ACCOUNTANT') && !selfBlocked;
  const fromAgent = r.requestedSource === 'AI' || r.requestedSource === 'MCP';

  return (
    <section id={r.id} className={cx('glass relative overflow-hidden rounded-2xl', focused && 'outline-2 outline-brand/40')}>
      {fromAgent && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-ai" />}
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <span className={cx('flex size-8 shrink-0 items-center justify-center rounded-md', fromAgent ? 'bg-ai-soft text-ai' : 'bg-sunken text-ink-2')}>{fromAgent ? <Icons.Sparkle size={16} /> : isBill ? <Icons.Bill size={16} /> : <Icons.Book size={16} />}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13.5px] font-medium">{title}</span>
            <SourceBadge source={r.requestedSource} />
            {r.status !== 'PENDING' && <StatusBadge status={r.status} />}
          </div>
          <div className="mt-0.5 truncate text-[12.5px] text-muted">
            {isEntry ? e.entry.memo : isBill ? e.bill.memo || `Due ${date(e.bill.dueDate)}` : r.reason} · requested by {actor(r.requestedBy)} {relative(r.createdAt)}
          </div>
        </div>
        {amount !== undefined && <span className="num text-[14px] font-semibold">{money(amount, currency)}</span>}
        <span className={cx('text-faint transition-transform', open && 'rotate-90')}>
          <Icons.Chevron size={15} />
        </span>
      </button>
      {open && (
        <div className="border-t border-line">
          <div className="flex flex-wrap gap-x-8 gap-y-2 bg-paper/60 px-4 py-2.5 text-[12.5px]">
            <span>
              <span className="text-muted">Why it is here: </span>
              {r.reason}
            </span>
            <span>
              <span className="text-muted">Approver: </span>
              {r.approverRole.toLowerCase()} or above
            </span>
            {isEntry && (
              <span>
                <span className="text-muted">Date: </span>
                {date(e.entry.date)} · {subs.byId.get(e.entry.subsidiaryId)?.name}
              </span>
            )}
            {r.decidedBy && (
              <span>
                <span className="text-muted">Decided by: </span>
                {actor(r.decidedBy)} {r.decidedAt && relative(r.decidedAt)}
                {r.comment && <span className="text-muted"> · “{r.comment}”</span>}
              </span>
            )}
          </div>
          {isEntry && <EntryLines lines={e.lines} currency={currency} />}
          {isBill && (
            <Table>
              <thead>
                <tr>
                  <Th>Line</Th>
                  <Th>Department</Th>
                  <Th>Service period</Th>
                  <Th right>Amount</Th>
                </tr>
              </thead>
              <tbody>
                {e.lines.map((l) => (
                  <tr key={l.id}>
                    <Td>
                      {l.description} {l.prepaid && <Badge tone="info">Prepaid</Badge>}
                    </Td>
                    <Td muted>{l.department}</Td>
                    <Td muted>{l.serviceStart ? `${date(l.serviceStart)} – ${date(l.serviceEnd)}` : '—'}</Td>
                    <Td right>{money(l.amount, currency)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="text-[12px] text-muted">
              {isEntry && (
                <Link to={`/journal/${e.entry.id}`} className="text-brand hover:underline">
                  Open entry →
                </Link>
              )}
              {isBill && (
                <Link to={`/ap/bills/${e.bill.id}`} className="text-brand hover:underline">
                  Open bill →
                </Link>
              )}
              {selfBlocked && r.status === 'PENDING' && <span className="ml-3 text-warn">You requested this, so someone else has to approve it (segregation of duties).</span>}
            </div>
            {r.status === 'PENDING' && (
              <div className="flex gap-2">
                <Button variant="danger" disabled={!canDecide} onClick={() => setRejecting(true)}>
                  Reject
                </Button>
                <Button variant="primary" disabled={!canDecide} loading={decide.isPending && decide.variables?.decision === 'APPROVE'} icon={<Icons.Check size={15} />} onClick={() => decide.mutate({ decision: 'APPROVE' })}>
                  Approve and post
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title="Reject this request"
        footer={
          <>
            <Button onClick={() => setRejecting(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={decide.isPending}
              onClick={() => decide.mutate({ decision: 'REJECT', comment: comment || undefined }, { onSuccess: () => setRejecting(false) })}
            >
              Reject
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Shared with the requester and kept in the audit log.">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Wrong department; book to Marketing instead" />
        </Field>
      </Modal>
    </section>
  );
}
